import { addDays, format } from 'date-fns';
import { utcDifferenceInDays } from '../../helpers/utcDifferenceInDays';
import { dbDayDateToken } from '../../static/dbDayDateToken';
import {
  EmploymentAvailability,
  EmploymentAvailabilityDay,
  EmploymentAvailabilityStatusEnum,
} from '../models/user';

export function createEmploymentAvailability(
  availability: EmploymentAvailability,
  employmentAvailabilities: EmploymentAvailability[],
  employmentAvailabilityDays: EmploymentAvailabilityDay[],
): {
  employmentAvailabilities: EmploymentAvailability[];
  employmentAvailabilityDays: EmploymentAvailabilityDay[];
} {
  const overlappingAvailabilities = employmentAvailabilities.filter((a) => {
    return (
      (a.startDate >= availability.startDate &&
        a.startDate <= availability.endDate) ||
      (a.endDate <= availability.endDate &&
        a.endDate >= availability.startDate) ||
      (availability.startDate >= a.startDate &&
        availability.startDate <= a.endDate) ||
      (availability.endDate >= a.startDate && availability.endDate <= a.endDate)
    );
  });

  if (overlappingAvailabilities?.length) {
    switch (availability.status) {
      case EmploymentAvailabilityStatusEnum.AVAILABLE:
        overlappingAvailabilities
          .filter((a) => availability.status === a.status)
          .forEach((a) => {
            if (a.startDate < availability.startDate)
              availability.startDate = a.startDate;
            if (a.endDate > availability.endDate)
              availability.endDate = a.endDate;
            if ((a.workload || 0) > (availability.workload || 0))
              availability.workload = a.workload;
            if (availability.notes && a.notes) {
              availability.notes = availability.notes + ' ' + a.notes;
            } else {
              availability.notes = availability.notes || a.notes;
            }
            const index = employmentAvailabilities.findIndex(
              (employmentAvailability) => employmentAvailability.id === a.id,
            );
            employmentAvailabilities.splice(index, 1);
            employmentAvailabilityDays = updateAvailabilityDays(
              a,
              employmentAvailabilityDays,
              true,
            );
          });
        employmentAvailabilities.push(availability);
        break;
      case EmploymentAvailabilityStatusEnum.UNAVAILABLE:
        overlappingAvailabilities
          .filter((a) => availability.status === a.status)
          .forEach((a) => {
            if (availability.unavailableReason === a.unavailableReason) {
              if (a.startDate < availability.startDate)
                availability.startDate = a.startDate;
              if (a.endDate > availability.endDate)
                availability.endDate = a.endDate;
              if (availability.notes && a.notes) {
                availability.notes = availability.notes + ' ' + a.notes;
              } else {
                availability.notes = availability.notes || a.notes;
              }
              const index = employmentAvailabilities.findIndex(
                (tempAvailability) => tempAvailability.id === a.id,
              );
              employmentAvailabilities.splice(index, 1);
              employmentAvailabilityDays = updateAvailabilityDays(
                a,
                employmentAvailabilityDays,
                true,
              );
            }
          });
        employmentAvailabilities.push(availability);
        break;
      case EmploymentAvailabilityStatusEnum.WORKING:
        if (!availability.bookedEmployment) break;
        overlappingAvailabilities
          .filter(
            (a) => a.status === EmploymentAvailabilityStatusEnum.UNAVAILABLE,
          )
          .forEach((a) => {
            if (!a.unavailableReason || !availability.bookedEmployment) return;

            if (!availability.bookedEmployment.unavailabilities)
              availability.bookedEmployment.unavailabilities = [];
            availability.bookedEmployment.unavailabilities.push({
              from: a.startDate,
              to: a.endDate,
              reason: a.unavailableReason,
            });
          });
        employmentAvailabilities.push(availability);
        break;
    }
  } else {
    employmentAvailabilities.push(availability);
  }

  return {
    employmentAvailabilities: employmentAvailabilities,
    employmentAvailabilityDays: updateAvailabilityDays(
      availability,
      employmentAvailabilityDays,
    ),
  };
}

export function updateEmploymentAvailability(
  availabilityId: string,
  availabilityInput: EmploymentAvailability,
  employmentAvailabilities: EmploymentAvailability[],
  employmentAvailabilityDays: EmploymentAvailabilityDay[],
): {
  employmentAvailabilities: EmploymentAvailability[];
  employmentAvailabilityDays: EmploymentAvailabilityDay[];
} {
  const availability = employmentAvailabilities.find(
    (availability) => availability.id === availabilityId,
  );
  if (!availability)
    return {
      employmentAvailabilities: employmentAvailabilities,
      employmentAvailabilityDays: employmentAvailabilityDays,
    };

  availability.startDate = availabilityInput.startDate;
  availability.endDate = availabilityInput.endDate;
  availability.status = availabilityInput.status;
  availability.notes = availabilityInput.notes;
  availability.unavailableReason = availabilityInput.unavailableReason;
  availability.bookedEmployment = availabilityInput.bookedEmployment;
  availability.workload = availabilityInput.workload;

  return {
    employmentAvailabilities: employmentAvailabilities,
    employmentAvailabilityDays: updateAvailabilityDays(
      availability,
      employmentAvailabilityDays,
    ),
  };
}

export function deleteEmploymentAvailability(
  availabilityId: string,
  employmentAvailabilities: EmploymentAvailability[],
  employmentAvailabilityDays: EmploymentAvailabilityDay[],
): {
  employmentAvailabilities: EmploymentAvailability[];
  employmentAvailabilityDays: EmploymentAvailabilityDay[];
} {
  const availability = employmentAvailabilities.find(
    (availability) => availability.id === availabilityId,
  );
  if (!availability)
    return {
      employmentAvailabilities: employmentAvailabilities,
      employmentAvailabilityDays: employmentAvailabilityDays,
    };

  const availabilityIndex = employmentAvailabilities.findIndex(
    (availability) => availability.id === availabilityId,
  );
  if (availabilityIndex !== undefined && availabilityIndex >= 0) {
    employmentAvailabilities.splice(availabilityIndex, 1);
  }

  return {
    employmentAvailabilities: employmentAvailabilities,
    employmentAvailabilityDays: updateAvailabilityDays(
      availability,
      employmentAvailabilityDays,
      true,
    ),
  };
}

export function updateAvailabilityDays(
  availability: EmploymentAvailability,
  employmentAvailabilityDays: EmploymentAvailabilityDay[],
  wasRemoved?: boolean,
): EmploymentAvailabilityDay[] {
  let startDate = availability.startDate;
  let endDate = availability.endDate;

  let daysWhichIncludeAvailability = employmentAvailabilityDays.filter((day) =>
    day.availabilityRanges?.some((range) => range.id === availability.id),
  );

  if (daysWhichIncludeAvailability.length) {
    let firstDayDate = daysWhichIncludeAvailability[0].date;
    let lastDayDate =
      daysWhichIncludeAvailability[daysWhichIncludeAvailability.length - 1]
        .date;

    if (startDate > firstDayDate) {
      startDate = firstDayDate;
    }
    if (endDate < lastDayDate) {
      endDate = lastDayDate;
    }
  }

  const daysBetween = utcDifferenceInDays(
    new Date(startDate),
    new Date(endDate),
  );

  for (let i = 0; i < daysBetween + 1; i++) {
    let date = format(addDays(new Date(startDate), i), dbDayDateToken);
    const dayAvailability = employmentAvailabilityDays.find(
      (day) => day.date === date,
    );

    if (wasRemoved && dayAvailability) {
      const rangeIndex = dayAvailability.availabilityRanges.findIndex(
        (range) => range.id === availability.id,
      );
      if (rangeIndex !== undefined && rangeIndex >= 0) {
        dayAvailability.availabilityRanges.splice(rangeIndex, 1);
      }
    } else {
      if (!dayAvailability) {
        employmentAvailabilityDays.push({
          date: date,
          status: availability.status,
          employmentId: availability.bookedEmployment?.employmentId,
          workload: availability.workload,
          availabilityRanges: [
            {
              id: availability.id,
              status: availability.status,
              employmentId: availability.bookedEmployment?.employmentId,
            },
          ],
        });
      } else {
        if (
          dayAvailability.date > availability.endDate ||
          dayAvailability.date < availability.startDate
        ) {
          let index = dayAvailability.availabilityRanges.findIndex(
            (range) => range.id === availability.id,
          );
          if (index !== undefined && index >= 0) {
            dayAvailability.availabilityRanges.splice(index, 1);
          }
        } else {
          const dayAvailabilityRange = dayAvailability.availabilityRanges.find(
            (range) => range.id === availability.id,
          );
          if (dayAvailabilityRange) {
            dayAvailabilityRange.workload = availability.workload;
            dayAvailabilityRange.status = availability.status;
          } else {
            dayAvailability.availabilityRanges.push({
              id: availability.id,
              status: availability.status,
              employmentId: availability.bookedEmployment?.employmentId,
              workload: availability.workload,
            });
          }
        }
      }
    }

    if (
      dayAvailability?.availabilityRanges?.some(
        (range) =>
          range.status === EmploymentAvailabilityStatusEnum.UNAVAILABLE,
      )
    ) {
      dayAvailability.status = EmploymentAvailabilityStatusEnum.UNAVAILABLE;
    } else if (
      dayAvailability?.availabilityRanges?.some(
        (range) => range.status === EmploymentAvailabilityStatusEnum.WORKING,
      )
    ) {
      dayAvailability.status = EmploymentAvailabilityStatusEnum.WORKING;
      dayAvailability.employmentId =
        availability.bookedEmployment?.employmentId;
    } else if (
      dayAvailability?.availabilityRanges?.some(
        (range) => range.status === EmploymentAvailabilityStatusEnum.AVAILABLE,
      )
    ) {
      dayAvailability.status = EmploymentAvailabilityStatusEnum.AVAILABLE;
      dayAvailability.workload = dayAvailability?.availabilityRanges?.sort(
        (a, b) => (b.workload || 0) - (a.workload || 0),
      )[0]?.workload;
    } else {
      const dayIndex = employmentAvailabilityDays.findIndex(
        (d) => d.date === dayAvailability?.date,
      );
      if (dayIndex !== undefined && dayIndex >= 0)
        employmentAvailabilityDays.splice(dayIndex, 1);
    }
  }
  return employmentAvailabilityDays;
}
